/*
 * Currency — sits on the right half of the top row, opposite the
 * Number-modeling section. Heading + running total at the top, then
 * the coin/bill add buttons, then a dashed empty tray (matching the
 * number-modeling bins) where placed coins land.
 */

.section {
  border-bottom: 1px solid rgba(8, 18, 12, 0.12);
  padding-bottom: var(--token-space-lg);
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
  gap: var(--token-space-md);
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--token-space-md);
}

.heading {
  font-family: var(--token-font-display);
  margin: 0;
  font-size: 33px;
  color: var(--token-text-primary);
}

/* "Clear coins" — sits at the right edge of the section header, same
 * ghost-pill treatment as the number-modeling "Clear blocks" button
 * so the two sides of the top row read as a matched pair. */
.clearButton {
  min-height: 36px;
  padding: 6px 14px;
  border-radius: var(--token-radius-pill);
  border: 1px solid rgba(8, 18, 12, 0.2);
  background: transparent;
  color: rgba(28, 38, 32, 0.7);
  font-family: var(--token-font-ui);
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
}

.clearButton:hover {
  background: rgba(8, 18, 12, 0.06);
  color: var(--token-text-primary);
}

.controls {
  display: flex;
  flex-wrap: wrap;
  gap: var(--token-space-md);
}

/* Each add-button stacks the actual coin/bill image above its label.
 * Tapping the whole thing drops a new instance into the tray below. */
.addButton {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  border-radius: 16px;
  border: 1.5px solid rgba(8, 18, 12, 0.14);
  background: rgba(255, 255, 255, 0.65);
  font-family: var(--token-font-ui);
  font-weight: 600;
  font-size: 13px;
  letter-spacing: 0.2px;
  color: var(--token-text-primary);
  cursor: pointer;
  transition:
    background 120ms ease,
    border-color 120ms ease,
    transform 120ms ease,
    box-shadow 120ms ease;
}

.addButton:hover {
  background: #fff;
  border-color: var(--token-accent);
  box-shadow: 0 6px 14px -6px rgba(8, 18, 12, 0.22);
}

.addButton:active {
  transform: translateY(1px);
}

.addImg {
  display: block;
  height: 60px;
  width: auto;
  object-fit: contain;
  pointer-events: none;
  user-select: none;
}

/* Dollar bill is rectangular — let it be wider than the round coins
 * so the bill artwork isn't cramped into a coin-sized box. */
.add_dollar .addImg {
  height: 44px;
}

.addLabel {
  font-family: var(--token-font-ui);
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: rgba(28, 38, 32, 0.78);
}

/* Empty bin where placed coins live. Matches the number-modeling
 * bins (dashed border, soft white fill, generous min-height) so the
 * two sides of the top row read as a matched pair. */
.tray {
  min-height: 200px;
  padding: 14px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.55);
  border: 1.5px dashed rgba(8, 18, 12, 0.18);
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: 10px;
}

/* Placed coin in the tray — the image is the whole tile. No fill or
 * border behind it, so the artwork reads naturally. Slight hover
 * lift so kids see it's tappable. */
.coin {
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 120ms ease;
}

.coin:hover {
  transform: translateY(-1px) scale(1.05);
}

/* Placed-coin sizing in the tray is tuned to be roughly life-size
 * relative to each other so kids see the real proportions: dime is
 * smallest, penny + nickel are small, quarter/half are mid-sized,
 * and the dollar BILL towers over the coins (it's a bill, not a
 * coin — bumped 2x per Sarah's note). The add buttons up top keep
 * a uniform size since they're labelled. */
.coinImg {
  display: block;
  height: 60px;
  width: auto;
  object-fit: contain;
  pointer-events: none;
  user-select: none;
}

.dime .coinImg {
  height: 44px;
}

.penny .coinImg {
  height: 48px;
}

.nickel .coinImg {
  height: 52px;
}

/* Half-dollar and quarter use the 60px default. */

.dollar .coinImg {
  height: 88px;
}
